import { CaretDownIcon } from "@phosphor-icons/react";
import { type RefObject, useRef, useState } from "react";
import type {
  CatalogCategoriesState,
  CatalogListState,
  CatalogManufacturersState,
  CatalogSubcategoriesState,
} from "../../hooks/useSourceCatalog";
import { formatCountLabel } from "../../lib/source-catalog";
import { CollapsibleDrawer } from "../CollapsibleDrawer";

const EXPANDED_WIDTH = "w-[320px]";

/**
 * A filter's checked options. Nothing checked is no filter; checking several
 * shows products matching any of them.
 */
interface FilterSelection {
  /** Manufacturer names, or category/subcategory ids. */
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
}

export type ManufacturerFilterState = CatalogManufacturersState &
  FilterSelection;

export type CategoryFilterState = CatalogCategoriesState & FilterSelection;

export type SubcategoryFilterState = CatalogSubcategoriesState &
  FilterSelection;

interface SourceFilterDrawerProps {
  manufacturers: ManufacturerFilterState;
  categories: CategoryFilterState;
  subcategories: SubcategoryFilterState;
}

const byIdOptions = (list: { id: string; name: string; count: number }[]) =>
  list.map(({ id, ...rest }) => ({ value: id, ...rest }));

/**
 * The Source screen's filter drawer. Starts open; collapsing it leaves the
 * 72px rail with the toggle, and keeps each section's own open/closed state
 * (see ``CollapsibleDrawer``). Order: Category, Subcategory (once a category
 * is selected), Brands.
 */
export function SourceFilterDrawer({
  manufacturers,
  categories,
  subcategories,
}: SourceFilterDrawerProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Starts expanded even on a reload with ?category=...; picking the first
  // category collapses it so Subcategory is immediately visible, and clearing
  // or unchecking the last category expands it again.
  const [isCategoryExpanded, setIsCategoryExpanded] = useState(true);
  const categoryHeadingRef = useRef<HTMLButtonElement>(null);

  const categoryState: CategoryFilterState = {
    ...categories,
    onToggle: (id) => {
      if (categories.selected.length === 0) {
        setIsCategoryExpanded(false);
        categoryHeadingRef.current?.focus();
      } else if (
        categories.selected.length === 1 &&
        categories.selected[0] === id
      ) {
        setIsCategoryExpanded(true);
      }
      categories.onToggle(id);
    },
  };

  return (
    <CollapsibleDrawer
      isCollapsed={isCollapsed}
      onCollapsedChange={setIsCollapsed}
      panel="filters"
      expandedWidth={EXPANDED_WIDTH}
      railClassName="flex px-6 py-[22px]"
      aria-label="Filters"
    >
      {(collapseToggle) => (
        <>
          <div className="flex h-[68px] shrink-0 items-center gap-4 px-6">
            <h3 className="text-heading-subhead flex-1">Filter by</h3>
            {collapseToggle}
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pt-4 pb-9">
            <FilterSection
              title="Category"
              noun="categories"
              state={categoryState}
              options={byIdOptions(categories.list)}
              isExpanded={isCategoryExpanded}
              onExpandedChange={setIsCategoryExpanded}
              headingRef={categoryHeadingRef}
            />
            {categories.selected.length > 0 && (
              <FilterSection
                title="Subcategory"
                noun="subcategories"
                state={{
                  ...subcategories,
                  onClear: () => {
                    subcategories.onClear();
                    setIsCategoryExpanded(true);
                  },
                }}
                options={byIdOptions(subcategories.list)}
              />
            )}
            <FilterSection
              title="Brands"
              noun="brands"
              state={manufacturers}
              options={manufacturers.list.map((m) => ({ value: m.name, ...m }))}
            />
          </div>
        </>
      )}
    </CollapsibleDrawer>
  );
}

/**
 * A drawer section whose heading folds its contents away, with a Clear
 * button beside it while anything in the section is checked.
 */
function FilterSection<T>({
  title,
  noun,
  state,
  options,
  isExpanded: controlledExpanded,
  onExpandedChange,
  headingRef,
}: {
  title: string;
  noun: string;
  state: CatalogListState<T> & FilterSelection;
  options: FilterOption[];
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  headingRef?: RefObject<HTMLButtonElement | null>;
}) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(true);
  const isExpanded = controlledExpanded ?? uncontrolledExpanded;
  const setIsExpanded = onExpandedChange ?? setUncontrolledExpanded;

  const handleClear = () => {
    setIsExpanded(true);
    state.onClear();
  };

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <button
          ref={headingRef}
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className="flex h-[34px] flex-1 items-center gap-2 rounded px-2 py-1 text-left hover:bg-white/5"
        >
          <span className="text-heading-subhead flex-1">{title}</span>
          <CaretDownIcon
            size={16}
            aria-hidden
            className={`shrink-0 text-[#a3afbc] transition-transform ${
              isExpanded ? "" : "-rotate-90"
            }`}
          />
        </button>
        {state.selected.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={`Clear ${title}`}
            className="px-2 text-[11px] leading-5 font-semibold text-[var(--accent-primary)] hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      {isExpanded && (
        <>
          <FilterListStatus state={state} noun={noun} />
          <CheckboxFilter title={title} selection={state} options={options} />
        </>
      )}
    </section>
  );
}

/**
 * Loading and failure states for one of the drawer's lists. A failed request
 * says so and offers a retry, rather than looking like an empty list.
 */
function FilterListStatus<T>({
  state,
  noun,
}: {
  state: CatalogListState<T>;
  /** What the list holds, as shown to users, e.g. "brands". */
  noun: string;
}) {
  if (state.isLoading) {
    return (
      <p className="px-2 text-[11px] leading-5 text-[#a3afbc]">
        Loading {noun}…
      </p>
    );
  }
  if (state.isError) {
    return (
      <p className="flex items-center gap-2 px-2 text-[11px] leading-5">
        <span className="text-[var(--accent-destructive)]">
          Couldn&apos;t load {noun}.
        </span>
        <button
          type="button"
          onClick={state.retry}
          className="font-semibold text-[var(--accent-primary)] hover:underline"
        >
          Try again
        </button>
      </p>
    );
  }
  return null;
}

const OPTION_LABEL =
  "flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[11px] leading-5 font-medium text-[#a3afbc] transition-colors hover:bg-white/5 hover:text-(--text-primary) has-checked:text-(--text-primary) has-disabled:cursor-default has-disabled:opacity-40 has-disabled:hover:bg-transparent has-disabled:hover:text-[#a3afbc]";

interface FilterOption {
  value: string;
  name: string;
  count: number;
}

/**
 * One section's options as checkboxes. An option whose count is zero would
 * show nothing given the other section's selection, so it is disabled rather
 * than hidden, keeping every row in place; a checked one stays enabled so it
 * can be unchecked.
 */
function CheckboxFilter({
  title,
  selection,
  options,
}: {
  title: string;
  selection: FilterSelection;
  options: FilterOption[];
}) {
  if (options.length === 0) return null;

  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="sr-only">{title}</legend>
      {options.map((option) => {
        const isChecked = selection.selected.includes(option.value);
        return (
          <label key={option.value} className={OPTION_LABEL}>
            <input
              type="checkbox"
              checked={isChecked}
              disabled={option.count === 0 && !isChecked}
              onChange={() => selection.onToggle(option.value)}
              className="accent-[#3c7ca5]"
            />
            {formatCountLabel(option)}
          </label>
        );
      })}
    </fieldset>
  );
}
